import { 
    API_ENDPOINTS, 
    Card, 
    CardBody, 
    customToast, 
    FormInput, 
    route, 
    SelectOption, 
    Spinner, 
    Stepper, 
    StringHelpers, 
    SubTitle,  
    useDisabledContext, 
    useLangContext, 
    useQueries, 
    Variants 
} from "carrier-fe";
import { useEffect, useMemo, useRef, useState } from "react";
import { ToshibaInstallationFormData } from "../../pages/installation/toshiba/journey";
import { ModuleNumbers, UseFormReturn } from "../../types/journey";
import { 
    MIN_NUMBER_OF_UNITS,
    ProductModule, 
    PRODUCT_CHILDREN, 
    PRODUCT_TYPES, 
    QUERY_KEYS, 
    PRODUCT_LINES
} from "../../util/Constants";
import { 
    fetchProductLines, 
    fetchProductModels, 
} from "../../util/toshiba-utils";
import { useParams, useSearchParams } from "react-router-dom";

type SelectCategory = 'product_types' | 'product_lines' | 'product_models';
    
type ProductSelects = Record<SelectCategory, Record<string, SelectOption[]>>;

function ToshibaStep5({
    form,
    loading,
    numberOfModules,
    setNumberOfModules,
    previousModuleDiffRef
}: {
    form: UseFormReturn<ToshibaInstallationFormData, any>,
    loading?: boolean,
    numberOfModules: ModuleNumbers,
    setNumberOfModules: React.Dispatch<React.SetStateAction<ModuleNumbers>>,
    previousModuleDiffRef: React.MutableRefObject<ModuleNumbers>
}) {
    const { store, setValues, errors } = form;
    const { installationId } = useParams();
    const [ searchParams ] = useSearchParams();
    const installationIdToUse = installationId || searchParams.get('installationId');
    const { fields, crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const [ productChild, setProductChild ] = useState<string>('');
    const [ selects, setSelects ] = useState<ProductSelects>({
        product_types: {},
        product_lines: {},
        product_models: {},
    });
    
    const nonA2aProducts = useMemo(() => 
        store?.products.filter(product => !product.is_a2a) ?? [], 
    [store?.products]);

    const firstProductLine = useMemo(() => 
        nonA2aProducts[0]?.product_line_id, 
    [nonA2aProducts]);

    const firstProductKey = useMemo(() => 
        nonA2aProducts[0]?.key, 
    [nonA2aProducts]);

    const firstProductTypes = useMemo(() => 
        selects.product_types[firstProductKey],
    [selects.product_types, firstProductKey]);

    const lastProductKey = useMemo(() => 
        nonA2aProducts[nonA2aProducts.length - 1]?.key, 
    [nonA2aProducts]);

    const lastProductTypes = useMemo(() => 
        selects.product_types[lastProductKey],
    [selects.product_types, lastProductKey]);

    const firstIndoorProduct = useMemo(() => {
        if (productChild === PRODUCT_CHILDREN.RAS_MULTI) {
            return nonA2aProducts[1];
        }
        if (productChild === PRODUCT_CHILDREN.RAV_MULTI) {
            return nonA2aProducts[0];
        }
        return null;
    }, [nonA2aProducts, productChild]);

    const domesticHotWaterFitted = useMemo(() => 
        productChild === PRODUCT_CHILDREN.ESTIA 
        && nonA2aProducts.length > MIN_NUMBER_OF_UNITS.ESTIA.DOMESTIC_HOT_WATER_NOT_FITTED, 
    [productChild, nonA2aProducts.length]);

    const [ numberOfUnits, setNumberOfUnits ] = useState<number>(
        nonA2aProducts.length
    );

    // Guards the RAS/RAV Single loaders against overlapping runs - two
    // interleaved loads leapfrogging each other's setValues can otherwise
    // re-trigger the effects indefinitely
    const childDataLoadingRef = useRef(false);

    const { 
        data: selectOptions, 
        error, 
        isLoading,
    } = useQueries<{ data: SelectOption[] }>([
        {
            key: QUERY_KEYS.PRODUCT_CHILDREN,
            config: {
                method: 'GET',
                url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.CHILDREN)
            },
        },
        {
            key: QUERY_KEYS.PRODUCT_TYPES,
            config: {
                method: 'GET',
                url: route(
                    API_ENDPOINTS.GENERIC.SELECT.PRODUCT.TYPES, {
                        ...store?.product_child_id && {
                            product_child_id: store?.product_child_id
                        },
                    }
                )
            },
        },
    ]);

    useEffect(() => {
        if (!selectOptions) return;

        const productTypes = selectOptions.product_types?.data ?? [];
        const productTypesMap = nonA2aProducts.reduce((acc, product) => {
            acc[product.key] = productTypes;
            return acc;
        }, {} as Record<string, SelectOption[]>);
        
        setSelects((prev) => ({
            ...prev,
            product_types: productTypesMap || {},
        }));

        const selectedProductChild = selectOptions.product_children?.data?.find(
                productChild => productChild.value === store?.product_child_id
            )?.label || '';

        setProductChild(selectedProductChild);
    }, [selectOptions, nonA2aProducts, store?.product_child_id]);

    useEffect(() => {
        if (!error) return;

        Object.values(error).map((e) =>
            customToast({ 
                title: (e?.response?.data as any)?.message, 
                variant: Variants.Danger 
            })
        );
    }, [error]);

    /* 
     * The following useEffects all prepare this step depending on 
     * which product child was selected on step 3
     */ 

    useEffect(() => {
        if (productChild !== PRODUCT_CHILDREN.RAS_SINGLE) return;

        if (nonA2aProducts.length > MIN_NUMBER_OF_UNITS.RAS_SINGLE) {
            setNumberOfUnits(MIN_NUMBER_OF_UNITS.RAS_SINGLE);
            return;
        };

        const outdoorProductKey = nonA2aProducts[1]?.key;
        const productTypes = selects.product_types[firstProductKey];

        const indoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.INDOORS
        );
        const outdoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.OUTDOORS
        );

        if (!indoorType 
            || !outdoorType 
            || !firstProductKey
            || !outdoorProductKey
        ) return;

        if (childDataLoadingRef.current) return;

        const loadRasSingleData = async () => {
            childDataLoadingRef.current = true;
            // product lines across indoor and outdoor types are the same for RAS Single
            try {
                const productLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: indoorType.value,
                    setDisabled
                });

                const productLinesMap = nonA2aProducts.reduce((acc, product) => {
                    acc[product.key] = productLines;
                    return acc;
                }, {} as Record<string, SelectOption[]>);

                let indoorProductModels: SelectOption[] = [];
                let outdoorProductModels: SelectOption[] = [];

                if (firstProductLine) {
                    indoorProductModels = await fetchProductModels({
                        childId: store?.product_child_id,
                        typeId: indoorType.value,
                        lineId: firstProductLine
                    });
                    outdoorProductModels = await fetchProductModels({
                        childId: store?.product_child_id,
                        typeId: outdoorType.value,
                        lineId: firstProductLine
                    });
                }

                setSelects(prev => ({
                    ...prev,
                    product_lines: productLinesMap,
                    product_models: {
                        [firstProductKey]: indoorProductModels,
                        [outdoorProductKey]: outdoorProductModels
                    }
                }));

                const updatedProducts = nonA2aProducts.map(product => ({
                    ...product,
                    product_type_id: product.key === firstProductKey ?
                        indoorType.value : outdoorType.value
                }));

                const typesChanged = nonA2aProducts.some((product) => {
                    const updatedProduct = updatedProducts.find(p => p.key === product.key);
                    return updatedProduct && product.product_type_id !== updatedProduct.product_type_id;
                });

                if (typesChanged) {
                    setValues({ products: updatedProducts });
                }
            } catch (error) {
                customToast({
                    title: (error as any)?.response?.data?.message || '',
                    variant: Variants.Danger,
                });
            } finally {
                childDataLoadingRef.current = false;
            }
        };

        loadRasSingleData();
    }, [productChild, nonA2aProducts.length, firstProductLine, firstProductTypes]);

    useEffect(() => {
        if (productChild !== PRODUCT_CHILDREN.RAS_MULTI) return;

        if (nonA2aProducts.length < MIN_NUMBER_OF_UNITS.RAS_MULTI) {
            const difference = MIN_NUMBER_OF_UNITS.RAS_MULTI - nonA2aProducts.length;
            const newProducts = [...nonA2aProducts];
            for (let i = 0; i < difference; i++) {
                newProducts.push(new ProductModule());
            }
            setNumberOfUnits(MIN_NUMBER_OF_UNITS.RAS_MULTI);
            setValues({ products: newProducts });
            return;
        }

        const productTypes = selects.product_types[firstProductKey];

        const indoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.INDOORS
        );
        const outdoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.OUTDOORS
        );

        const hasProductLines = nonA2aProducts.every(product => 
            selects.product_lines[product.key]?.length > 0
        );

        if (!indoorType || !outdoorType || hasProductLines) return;

        const loadRasMultiData = async () => {
            try {
                const outdoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: outdoorType.value,
                    setDisabled
                });
                const indoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: indoorType.value,
                    setDisabled
                });

                const productLinesMap = nonA2aProducts.reduce((acc, product) => {
                    acc[product.key] = product.key === firstProductKey ? 
                        outdoorProductLines : indoorProductLines;
                    return acc;
                }, {} as Record<string, SelectOption[]>);

                const rasMultiOutdoorLine = outdoorProductLines.find(
                    line => line.label === 'RAS Outdoor Multi'
                );

                if (!rasMultiOutdoorLine) return;

                const outdoorProductModels: SelectOption[] = await fetchProductModels({
                    childId: store?.product_child_id,
                    typeId: outdoorType.value,
                    lineId: rasMultiOutdoorLine.value,
                    setDisabled
                });

                setSelects(prev => ({
                    ...prev,
                    product_lines: productLinesMap,
                    product_models: {
                        ...prev.product_models,
                        [firstProductKey]: outdoorProductModels
                    },
                }));

                const updatedProducts = nonA2aProducts.map(product => 
                    product.key === firstProductKey ? 
                        { ...product, 
                            product_type_id: outdoorType.value, 
                            product_line_id: rasMultiOutdoorLine.value 
                        }
                    : 
                        { ...product, 
                            product_type_id: indoorType.value 
                        }
                );

                const productsChanged = nonA2aProducts.some((product) => {
                    const updatedProduct = updatedProducts.find(p => p.key === product.key);
                    return updatedProduct && (
                        product.product_type_id !== updatedProduct.product_type_id ||
                        product.product_line_id !== updatedProduct.product_line_id
                    );
                });

                if (productsChanged) {
                    setValues({ products: updatedProducts });
                }
            } catch (error) {
                customToast({
                    title: (error as any)?.response?.data?.message || '',
                    variant: Variants.Danger,
                });
            }
        };

        loadRasMultiData();
    }, [productChild, nonA2aProducts.length, firstProductTypes]);

    useEffect(() => {
        if (productChild !== PRODUCT_CHILDREN.RAV_SINGLE) return;

        if (nonA2aProducts.length > MIN_NUMBER_OF_UNITS.RAV_SINGLE) {
            setNumberOfUnits(MIN_NUMBER_OF_UNITS.RAV_SINGLE);
            return;
        };

        const outdoorProductKey = nonA2aProducts[1]?.key;
        const productTypes = selects.product_types[firstProductKey];

        const indoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.INDOORS
        );
        const outdoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.OUTDOORS
        );

        const hasProductLines = nonA2aProducts.every(product =>
            selects.product_lines[product.key]?.length > 0
        );

        if (!indoorType
            || !outdoorType
            || !firstProductKey
            || !outdoorProductKey
            || hasProductLines
            || childDataLoadingRef.current
        ) return;

        const loadRavSingleData = async () => {
            childDataLoadingRef.current = true;
            try {
                const indoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: indoorType.value,
                    setDisabled
                });
                const outdoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: outdoorType.value,
                    setDisabled
                });

                setSelects(prev => ({
                    ...prev,
                    product_lines: {
                        [firstProductKey]: indoorProductLines,
                        [outdoorProductKey]: outdoorProductLines,
                    }
                }));

                const updatedProducts = nonA2aProducts.map(product => ({
                    ...product,
                    product_type_id: product.key === firstProductKey ?
                        indoorType.value : outdoorType.value
                }));

                const typesChanged = nonA2aProducts.some((product) => {
                    const updatedProduct = updatedProducts.find(p => p.key === product.key);
                    return updatedProduct && product.product_type_id !== updatedProduct.product_type_id;
                });

                if (typesChanged) {
                    setValues({ products: updatedProducts });
                }
            } catch (error) {
                customToast({
                    title: (error as any)?.response?.data?.message || '',
                    variant: Variants.Danger,
                });
            } finally {
                childDataLoadingRef.current = false;
            }
        };

        loadRavSingleData();
    }, [productChild, nonA2aProducts.length, firstProductTypes]);

    useEffect(() => {
        if (productChild !== PRODUCT_CHILDREN.RAV_MULTI) return;

        if (nonA2aProducts.length < MIN_NUMBER_OF_UNITS.RAV_MULTI) {
            const difference = MIN_NUMBER_OF_UNITS.RAV_MULTI - nonA2aProducts.length;
            const newProducts = [...nonA2aProducts];
            for (let i = 0; i < difference; i++) {
                newProducts.push(new ProductModule());
            }
            setNumberOfUnits(MIN_NUMBER_OF_UNITS.RAV_MULTI);
            setValues({ products: newProducts });
            return;
        }

        const lastProductIndex = nonA2aProducts.length - 1;
        const outdoorProductKey = nonA2aProducts[lastProductIndex]?.key;
        const productTypes = selects.product_types[outdoorProductKey];

        const indoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.INDOORS
        );
        const outdoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.OUTDOORS
        );

        const hasProductLines = nonA2aProducts.every(product => 
            selects.product_lines[product.key]?.length > 0
        );

        if (!indoorType || !outdoorType || hasProductLines) return;

        const loadRavMultiData = async () => {
            try {
                const outdoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: outdoorType.value,
                    setDisabled
                });
                const indoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: indoorType.value,
                    setDisabled
                });

                const productLinesMap = nonA2aProducts.reduce((acc, product) => {
                    acc[product.key] = product.key === outdoorProductKey ? 
                        outdoorProductLines : indoorProductLines;
                    return acc;
                }, {} as Record<string, SelectOption[]>);

                setSelects(prev => ({
                    ...prev,
                    product_lines: productLinesMap
                }));

                const updatedProducts = nonA2aProducts.map(product => ({
                    ...product,
                    product_type_id: product.key === outdoorProductKey ? 
                        outdoorType.value : indoorType.value,
                }));

                const typesChanged = nonA2aProducts.some((product) => {
                    const updatedProduct = updatedProducts.find(p => p.key === product.key);
                    return updatedProduct && product.product_type_id !== updatedProduct.product_type_id;
                });

                if (typesChanged) {
                    setValues({ products: updatedProducts });
                }
            } catch (error) {
                customToast({
                    title: (error as any)?.response?.data?.message || '',
                    variant: Variants.Danger,
                });
            }
        };

        loadRavMultiData();
    }, [productChild, nonA2aProducts.length, lastProductTypes]);

    useEffect(() => {
        if (productChild !== PRODUCT_CHILDREN.VRF) return;

        const productTypes = selects.product_types[firstProductKey];

        const indoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.INDOORS
        );
        const outdoorType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.OUTDOORS
        );

        if (!indoorType || !outdoorType) return;

        if (nonA2aProducts.length !== (numberOfModules.indoor + numberOfModules.outdoor) 
            && installationIdToUse
        ) {
            const hasTypedProducts = nonA2aProducts.some(
                product => product.product_type_id
            );

            if (!hasTypedProducts) {
                setNumberOfModules({
                    indoor: MIN_NUMBER_OF_UNITS.VRF.INDOOR,
                    outdoor: MIN_NUMBER_OF_UNITS.VRF.OUTDOOR,
                });
                previousModuleDiffRef.current = { indoor: 0, outdoor: 0 };
                return;
            }
            
            const numberOfOutdoorModules = nonA2aProducts.filter(
                product => product.product_type_id === outdoorType.value
            ).length;

            const numberOfIndoorModules = nonA2aProducts.filter(
                product => product.product_type_id === indoorType.value
            ).length;
            
            setNumberOfModules({
                indoor: numberOfIndoorModules,
                outdoor: numberOfOutdoorModules
            });

            previousModuleDiffRef.current = {
                indoor: numberOfIndoorModules - MIN_NUMBER_OF_UNITS.VRF.INDOOR,
                outdoor: numberOfOutdoorModules - MIN_NUMBER_OF_UNITS.VRF.OUTDOOR,
            };
            
            return;
        }

        const loadVRFData = async () => {
            try {
                const outdoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: outdoorType.value,
                    setDisabled
                });
                const indoorProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: indoorType.value,
                    setDisabled
                });
                const productLinesMap = nonA2aProducts.reduce((acc, product, index) => {
                    acc[product.key] = index < numberOfModules.outdoor ? 
                        outdoorProductLines : indoorProductLines;
                    return acc;
                }, {} as Record<string, SelectOption[]>);

                const outdoorProductModels: SelectOption[] = await fetchProductModels({
                    childId: store?.product_child_id,
                    typeId: outdoorType.value,
                    lineId: firstProductLine,
                    setDisabled
                });
                const productModelsMap = nonA2aProducts.reduce((acc, product, index) => {
                    if (index < numberOfModules.outdoor) {
                        acc[product.key] = outdoorProductModels
                    }
                    return acc;
                }, {} as Record<string, SelectOption[]>);

                setSelects(prev => ({
                    ...prev,
                    product_lines: productLinesMap,
                    product_models: {
                        ...prev.product_models,
                        ...productModelsMap
                    }
                }));

                const updatedProducts = nonA2aProducts.map((product, index) => 
                    index < numberOfModules.outdoor ? 
                        { ...product, 
                            product_type_id: outdoorType.value, 
                            product_line_id: firstProductLine 
                        }
                    : 
                        { ...product, 
                            product_type_id: indoorType.value,
                        }
                );

                const typesChanged = nonA2aProducts.some((product) => {
                    const updatedProduct = updatedProducts.find(p => p.key === product.key);
                    return updatedProduct && product.product_type_id !== updatedProduct.product_type_id;
                });

                if (typesChanged) {
                    setValues({ products: updatedProducts });
                }
            } catch (error) {
                customToast({
                    title: (error as any)?.response?.data?.message || '',
                    variant: Variants.Danger,
                });
            }
        };

        loadVRFData();
    }, [productChild, nonA2aProducts?.length, firstProductLine, firstProductTypes]);

    useEffect(() => {
        if (productChild !== PRODUCT_CHILDREN.ESTIA) return;

        if (nonA2aProducts.length > MIN_NUMBER_OF_UNITS.ESTIA.DOMESTIC_HOT_WATER_FITTED) {
            const difference = 
                nonA2aProducts.length - MIN_NUMBER_OF_UNITS.ESTIA.DOMESTIC_HOT_WATER_FITTED;
            setNumberOfUnits(nonA2aProducts.length - difference);        
        }

        const hydroProductKey = nonA2aProducts[1]?.key;
        const cylinderProductKey = nonA2aProducts[2]?.key;
        const productTypes = selects.product_types[firstProductKey];

        const standaloneType = productTypes?.find(
            type => type.label === PRODUCT_TYPES.STANDALONE
        );

        const hasProductLines = nonA2aProducts.every(product => 
            selects.product_lines[product.key]?.length > 0
        );

        if (!standaloneType || hasProductLines) return;

        const loadEstiaData = async () => {
            try {
                const standaloneProductLines: SelectOption[] = await fetchProductLines({
                    childId: store?.product_child_id,
                    typeId: standaloneType.value,
                    setDisabled
                });

                const productLinesMap = nonA2aProducts.reduce((acc, product) => {
                    acc[product.key] = standaloneProductLines;
                    return acc;
                }, {} as Record<string, SelectOption[]>);

                const outdoorProductLine = standaloneProductLines.find(
                    line => line.label === PRODUCT_LINES.OUTDOOR
                );
                const hydroProductLine = standaloneProductLines.find(
                    line => line.label === PRODUCT_LINES.HYDRO
                );
                const cylinderProductLine = standaloneProductLines.find(
                    line => line.label === PRODUCT_LINES.CYLINDER
                );

                if (!outdoorProductLine || !hydroProductLine) return;

                const outdoorProductModels: SelectOption[] = await fetchProductModels({
                    childId: store?.product_child_id,
                    typeId: standaloneType.value,
                    lineId: outdoorProductLine.value
                });
                const hydroProductModels: SelectOption[] = await fetchProductModels({
                    childId: store?.product_child_id,
                    typeId: standaloneType.value,
                    lineId: hydroProductLine.value
                });
                const cylinderProductModels: SelectOption[] = await fetchProductModels({
                    childId: store?.product_child_id,
                    typeId: standaloneType.value,
                    lineId: cylinderProductLine?.value
                });

                setSelects(prev => ({
                    ...prev,
                    product_lines: productLinesMap,
                    product_models: {
                        ...prev.product_models,
                        [firstProductKey]: outdoorProductModels,
                        [hydroProductKey]: hydroProductModels,
                        [cylinderProductKey]: cylinderProductModels,
                    }
                }));

                const updatedProducts = nonA2aProducts.map(product => ({
                    ...product,
                    product_type_id: standaloneType.value,
                    product_line_id: product.key === firstProductKey
                        ? outdoorProductLine.value
                        : product.key === hydroProductKey
                        ? hydroProductLine.value
                        : cylinderProductLine?.value 
                            || product.product_line_id
                }));

                const productsChanged = nonA2aProducts.some((product) => {
                    const updatedProduct = updatedProducts.find(p => p.key === product.key);
                    return updatedProduct && (
                        product.product_type_id !== updatedProduct.product_type_id ||
                        product.product_line_id !== updatedProduct.product_line_id
                    );
                });

                if (productsChanged) {
                    setValues({ products: updatedProducts });
                }
            } catch (error) {
                customToast({
                    title: (error as any)?.response?.data?.message || '',
                    variant: Variants.Danger,
                });
            }
        };

        loadEstiaData();
    }, [productChild, nonA2aProducts.length, firstProductTypes, domesticHotWaterFitted]);

    // Update length of products array
    useEffect(() => {
        const unitNumberDifference = numberOfUnits - nonA2aProducts.length;
        let newProducts = [...nonA2aProducts];

        // RAS Multi indoor units each choose their own product line and model, so
        // new units inherit only the indoor product type and start blank, rather
        // than duplicating the first indoor unit's product line and model.
        const isRasMulti = productChild === PRODUCT_CHILDREN.RAS_MULTI;

        if (unitNumberDifference > 0) {
            for (let i = 0; i < unitNumberDifference; i++) {
                const newModule = new ProductModule(
                    firstIndoorProduct ? (
                        isRasMulti ? {
                            product_type_id: firstIndoorProduct.product_type_id,
                        } : {
                            product_type_id: firstIndoorProduct.product_type_id,
                            product_line_id: firstIndoorProduct.product_line_id,
                            product_id: firstIndoorProduct.product_id,
                        }
                    ) : {}
                );

                productChild === PRODUCT_CHILDREN.RAV_MULTI
                ? newProducts.unshift(newModule)
                : newProducts.push(newModule)
            }

            // Sync product_lines and product_models selects for new indoor products
            if (firstIndoorProduct) {
                const firstIndoorLines = selects.product_lines[firstIndoorProduct.key] || [];
                const firstIndoorModels = selects.product_models[firstIndoorProduct.key] || [];

                setSelects(prev => ({
                    ...prev,
                    product_lines: {
                        ...prev.product_lines,
                        ...newProducts.reduce((acc, p) => {
                            if (!prev.product_lines[p.key]) {
                                // Indoor units share the same product line options,
                                // but each unit selects its own line independently.
                                acc[p.key] = firstIndoorLines;
                            }
                            return acc;
                        }, {} as Record<string, SelectOption[]>),
                    },
                    product_models: {
                        ...prev.product_models,
                        ...newProducts.reduce((acc, p) => {
                            if (!prev.product_models[p.key]) {
                                // RAS Multi units fetch their own models once a
                                // product line is picked, so start them empty.
                                acc[p.key] = isRasMulti ? [] : firstIndoorModels;
                            }
                            return acc;
                        }, {} as Record<string, SelectOption[]>),
                    },
                }));
            }
        } else {
            const itemsToRemove = Math.abs(unitNumberDifference);

            productChild === PRODUCT_CHILDREN.RAV_MULTI 
            ? newProducts.splice(0, itemsToRemove)
            : newProducts = newProducts.slice(0, newProducts.length - itemsToRemove)
        }

        setValues({
            products: newProducts
        });

    }, [numberOfUnits]);

    // Update length of products array for VRF with indoor and outdoor independently 
    useEffect(() => {
        if (!numberOfModules.indoor) return;

        const currentOutdoorModuleDiff = 
            numberOfModules.outdoor - MIN_NUMBER_OF_UNITS.VRF.OUTDOOR;
        const previousOutdoorModuleDiff = previousModuleDiffRef.current.outdoor;

        const currentIndoorModuleDiff = 
            numberOfModules.indoor - MIN_NUMBER_OF_UNITS.VRF.INDOOR;
        const previousIndoorModuleDiff = previousModuleDiffRef.current.indoor;

        const newProducts = [...nonA2aProducts];

        // Handle outdoor modules
        const outdoorDifference = Math.abs(currentOutdoorModuleDiff - previousOutdoorModuleDiff);
        
        if (currentOutdoorModuleDiff > previousOutdoorModuleDiff) {
            const insertIndex = previousOutdoorModuleDiff + 1;
            const newModules = Array.from(
                { length: outdoorDifference }, 
                () => new ProductModule()
            );
            newProducts.splice(insertIndex, 0, ...newModules);
        } else if (currentOutdoorModuleDiff < previousOutdoorModuleDiff) {
            const removeIndex = currentOutdoorModuleDiff + 1;
            newProducts.splice(removeIndex, outdoorDifference);
        }

        // Handle indoor modules
        const indoorDifference = Math.abs(currentIndoorModuleDiff - previousIndoorModuleDiff);
        
        if (currentIndoorModuleDiff > previousIndoorModuleDiff) {
            const newModules = Array.from(
                { length: indoorDifference }, 
                () => new ProductModule()
            );
            newProducts.push(...newModules);
        } else if (currentIndoorModuleDiff < previousIndoorModuleDiff) {
            newProducts.splice(
                newProducts.length - indoorDifference, 
                indoorDifference
            );
        }

        setValues({ products: newProducts });

        previousModuleDiffRef.current = {
            indoor: currentIndoorModuleDiff,
            outdoor: currentOutdoorModuleDiff
        };
    }, [numberOfModules]);

    /**
     * fetch missing product model selects option arrays when revisiting this step. 
     * If a model was previously selected the selection will not render without this
     */
    useEffect(() => {
        const productsNeedingModels = nonA2aProducts.filter(product => 
            product.product_line_id 
        );

        handleBatchProductModelFetch(productsNeedingModels);
    }, []);

    async function handleBatchProductModelFetch(
        productsNeedingModels: ProductModule[]
    ) {
        if (productsNeedingModels.length === 0) return;

        try {
            const modelFetchPromises = productsNeedingModels.map(product => 
                fetchProductModels({
                    childId: store?.product_child_id,
                    typeId: product.product_type_id,
                    lineId: product.product_line_id,
                    setDisabled
                }).then(models => ({ productKey: product.key, models }))
            );

            const fetchedModels = await Promise.all(modelFetchPromises);

            const productModelsMap = fetchedModels.reduce(
                (acc, { productKey, models }) => {
                    acc[productKey] = models;
                    return acc;
                }, 
                {} as Record<string, SelectOption[]>
            );

            setSelects((prev) => ({
                ...prev,
                product_models: {
                    ...prev.product_models,
                    ...productModelsMap,
                }
            }));
        } catch (error) {
            customToast({
                title: (error as any)?.response?.data?.message || '',
                variant: Variants.Danger,
            });
        }
    };

    const handleProductLineChange = (
        product: ProductModule,
        productLineId: string,
    ) => {
        const lastProductIndex = nonA2aProducts.length - 1;
        const lastProductKey = nonA2aProducts[lastProductIndex].key;

        fetchProductModels({
            childId: store?.product_child_id,
            typeId: product.product_type_id,
            lineId: productLineId,
            setDisabled
        }).then(productModels => {
            const productModelsMap = nonA2aProducts.reduce(
                (acc, prdct) => {
                    if (productChild === PRODUCT_CHILDREN.RAV_MULTI) {
                        // Last product (outdoor) - gets its own models
                        if (prdct.key === lastProductKey) {
                            acc[prdct.key] = prdct.key === product.key 
                                ? productModels 
                                : selects.product_models[prdct.key] || [];
                        }
                        // Indoor products - all share the same models
                        else {
                            acc[prdct.key] = product.key !== lastProductKey
                                ? productModels
                                : selects.product_models[prdct.key] || [];
                        }
                    } else {
                        // RAS Multi and other product children - each unit keeps
                        // its own independently selected product line and models
                        acc[prdct.key] = prdct.key === product.key
                            ? productModels
                            : selects.product_models[prdct.key] || [];
                    }
                    return acc;
                }, 
                {} as Record<string, SelectOption[]>
            );
           
            setSelects((prev) => ({
                ...prev,
                product_models: {
                    ...prev.product_models,
                    ...productModelsMap,
                }
            }));
        }).catch(error => {
            customToast({
                title: error?.response?.data?.message || '',
                variant: Variants.Danger,
            });
        });

        const updatedProducts = nonA2aProducts?.map((prdct) => {
            const shouldUpdateAllIndoorProducts = (
                productChild === PRODUCT_CHILDREN.RAV_MULTI
                && product.key !== lastProductKey
                && prdct.key !== lastProductKey
            );

            if (
                shouldUpdateAllIndoorProducts
                || prdct.key === product.key
            ) {
                return {
                    ...prdct,
                    product_line_id: productLineId,
                    product_id: ''
                };
            }

            return prdct;
        });

        setValues({ products: updatedProducts });
    };

    /*
     * The following functions dynamically render subtitles depending on user input
     */

    const getSingularTypeLabel = (typeId: string): string => {
        const typeLabel = selectOptions?.product_types?.data
            ?.find(type => type.value === typeId)
            ?.label || '';
        return typeLabel.replace(/s$/, '');
    };

    const getProductNoun = (productIndex: number): string => {
        const unitLabel = crud?.sub_titles?.unit || 'unit';
        
        // For VRF outdoor modules, use Header/Follower terminology
        if (
            productChild === PRODUCT_CHILDREN.VRF 
            && productIndex < numberOfModules.outdoor
        ) {
            return productIndex === 0 
                ? fields?.header || 'Header'
                : fields?.follower || 'Follower';
        }
        
        return unitLabel;
    };

    const getUnitNumber = (productIndex: number): string => {
        // RAV Multi: number all but the last product
        if (
            productChild === PRODUCT_CHILDREN.RAV_MULTI 
            && productIndex !== nonA2aProducts?.length - 1
        ) {
            return String(productIndex + 1);
        }

        // RAS Multi: number all but the first product
        if (
            productChild === PRODUCT_CHILDREN.RAS_MULTI 
            && productIndex !== 0
        ) {
            return String(productIndex);
        }

        // VRF: separate numbering for outdoor and indoor modules
        if (productChild === PRODUCT_CHILDREN.VRF) {
            if (productIndex > 0 && productIndex < numberOfModules.outdoor) {
                return String(productIndex);
            }
            if (productIndex >= numberOfModules.outdoor) {
                return String(productIndex - numberOfModules.outdoor + 1);
            }
        }

        return '';
    };

    const getLineLabel = (productKey: string, lineId: string): string => {
        return selects.product_lines[productKey]
            ?.find(line => line.value === lineId)
            ?.label || '';
    };

    const getProductSubtitle = (
        productKey: string,
        productTypeId: string,
        productLineId: string, 
        productIndex: number, 
    ): string => {
        const singularType = getSingularTypeLabel(productTypeId);
        const noun = getProductNoun(productIndex);
        const unitNumber = getUnitNumber(productIndex);

        if (!singularType) return crud?.buttons?.loading || 'Loading...';

        // For ESTIA products, include the line label (Outdoor, Hydro, Cylinder)
        if (productChild === PRODUCT_CHILDREN.ESTIA) {
            const lineLabel = getLineLabel(productKey, productLineId);
            return `${lineLabel} ${singularType} ${noun}`.trim();
        }
        
        return `${singularType} ${noun} ${unitNumber}`.trim();
    };

    const getStepTitle = (): string => {
        const systemLabel = crud?.sub_titles?.system || 'System';
        const outdoorLabel = fields?.outdoor || 'Outdoor';
        
        if (productChild === PRODUCT_CHILDREN.VRF) {
            return `${outdoorLabel} ${productChild}`.trim();
        }
        
        return `${productChild} ${systemLabel}`.trim();
    };

    const isProductLineDisabled = (index: number): boolean => {
        // RAV Multi: disable indoor lines except first indoor (index 0)
        if (
            productChild === PRODUCT_CHILDREN.RAV_MULTI
            && index !== nonA2aProducts.length - 1
            && index !== 0
        ) return true;

        // RAV Multi outdoor: disable until first indoor has line + model
        if (
            productChild === PRODUCT_CHILDREN.RAV_MULTI
            && index === nonA2aProducts.length - 1
            && (!firstProductLine || !firstIndoorProduct?.product_id)
        ) return true;

        return disabled || isLoading;
    };

    const isProductModelDisabled = (product: ProductModule, index: number): boolean => {
        // VRF outdoor: disable until shared product line is selected
        if (
            productChild === PRODUCT_CHILDREN.VRF
            && index < numberOfModules.outdoor
            && !firstProductLine
        ) return true;

        // RAS Single: disable until shared product line is selected
        if (
            productChild === PRODUCT_CHILDREN.RAS_SINGLE
            && !firstProductLine
        ) return true;

        // RAV Single: disable until product's own line is selected
        if (
            productChild === PRODUCT_CHILDREN.RAV_SINGLE
            && !product.product_line_id
        ) return true;

        // RAV Multi first indoor: disable until its product line is selected
        if (
            productChild === PRODUCT_CHILDREN.RAV_MULTI
            && index === 0
            && !product.product_line_id
        ) return true;

        // RAV Multi non-first indoor: always disabled (synced from first indoor)
        if (
            productChild === PRODUCT_CHILDREN.RAV_MULTI
            && index !== nonA2aProducts.length - 1
            && index !== 0
        ) return true;

        // RAV Multi outdoor: disable until first indoor has line + model AND outdoor line selected
        if (
            productChild === PRODUCT_CHILDREN.RAV_MULTI
            && index === nonA2aProducts.length - 1
            && (!firstProductLine || !firstIndoorProduct?.product_id || !product.product_line_id)
        ) return true;

        return disabled || isLoading;
    };

    if (isLoading) return (
        <div className="d-flex justify-content-center align-items-center vh-100">
            <Spinner />
        </div>
    );

    return (
        <Stepper.Step
            loading={isLoading || loading}
            title={getStepTitle()}
            titleNotCapitalised
        >
            {productChild === PRODUCT_CHILDREN.RAS_SINGLE && 
                <FormInput
                    name='product_line_id'
                    type='searchable-select'
                    label={crud?.models?.product_line
                        || 'Product Line'
                    }
                    options={selects.product_lines?.[firstProductKey] || []}
                    value={firstProductLine}
                    onChange={(val) => {
                        if (typeof val !== 'string') return;

                        setValues({
                            products: nonA2aProducts?.map(product => ({ 
                                ...product,
                                product_line_id: val, 
                                product_id: '' 
                            }))
                        });
                    }}
                    clearable={false}
                    disabled={disabled || isLoading}
                    errorMessages={errors?.[`products.0.product_line_id`]}
                />
            }
            {productChild === PRODUCT_CHILDREN.RAV_MULTI &&
                <FormInput
                    name='indoor_combination'
                    type='select'
                    label={
                        fields?.indoor_combination
                        || 'Indoor Combination'
                    }
                    options={crud?.options?.indoor_combination}
                    value={String(nonA2aProducts.length - 1)}
                    onChange={value => {
                        const numberOfIndoorUnits = Number(value);
                        const totalNumberOfUnitsRequired = numberOfIndoorUnits + 1;
                        
                        if (nonA2aProducts.length < totalNumberOfUnitsRequired) {
                            setNumberOfUnits(totalNumberOfUnitsRequired);
                        } else if (nonA2aProducts.length > totalNumberOfUnitsRequired) {
                            const difference = nonA2aProducts.length - totalNumberOfUnitsRequired;
                            setNumberOfUnits(nonA2aProducts.length - difference);
                        }
                    }}
                    disabled={disabled || isLoading}
                />
            }
            {productChild === PRODUCT_CHILDREN.VRF && 
                <div className='row'>
                    <div className='col'>
                        <FormInput
                            name='product_line_id'
                            type='searchable-select'
                            label={crud?.models?.product_line
                                || 'Product Line'
                            }
                            options={selects.product_lines?.[firstProductKey] || []}
                            value={firstProductLine}
                            onChange={(val) => {
                                if (typeof val !== 'string') return;

                                setValues({
                                    products: nonA2aProducts?.map((product, index) => 
                                        index < numberOfModules.outdoor
                                        ? { ...product, product_line_id: val, product_id: '' }
                                        : product
                                    )       
                                });
                            }}
                            clearable={false}
                            disabled={disabled || isLoading}
                            errorMessages={errors?.[`products.0.product_line_id`]}
                        />
                    </div>
                    
                    <div className='col'>
                        <FormInput
                            name='outdoor_modules'
                            type='searchable-select'
                            label={
                                fields?.number_of_modules
                                || 'Number of modules'
                            }
                            options={crud?.options?.number_of_modules}
                            value={String(numberOfModules.outdoor)}
                            onChange={value => setNumberOfModules(
                                prev => ({
                                    ...prev,
                                    outdoor: Number(value)
                                })
                            )}
                            clearable={false}
                            disabled={disabled || isLoading}
                        />
                    </div>
                </div>
            }
            {nonA2aProducts?.map((product, index) => 
                <div key={product.key}>
                    {productChild === PRODUCT_CHILDREN.VRF
                        && index === numberOfModules.outdoor
                        && (
                            <>
                                <SubTitle
                                    title={`${fields?.indoor || 'Indoor'} ${productChild}`.trim()}
                                    style={{ color: '#464C5E', fontSize: 32 }}
                                />
                                <FormInput
                                    name='indoor_modules'
                                    type='text'
                                    inputMode='numeric'
                                    label={
                                        fields?.quantity_of_units
                                        || 'Quantity of units'
                                    }
                                    value={numberOfModules.indoor 
                                        ? String(numberOfModules.indoor) 
                                        : ''
                                    }
                                    onChange={value => {
                                        if (typeof value === 'string' 
                                            // Only allow digits
                                            && !/^\d*$/.test(value)) {
                                            return; 
                                        }

                                        const number = Number(value);
                                        if (number >= 0 && number <= 50) {
                                            setNumberOfModules(
                                                prev => ({
                                                    ...prev,
                                                    indoor: number
                                                })
                                            )
                                        }
                                    }}
                                    disabled={disabled || isLoading}
                                />
                            </>
                        )
                    }
                    <Card>
                        <CardBody>
                            <SubTitle 
                                title={getProductSubtitle(
                                    product.key,
                                    product.product_type_id, 
                                    product.product_line_id,
                                    index, 
                                )} 
                            /> 
                            {(productChild === PRODUCT_CHILDREN.RAV_SINGLE
                                || productChild === PRODUCT_CHILDREN.RAV_MULTI
                                || (productChild === PRODUCT_CHILDREN.RAS_MULTI
                                    && index > 0)
                                || (productChild === PRODUCT_CHILDREN.VRF 
                                    && index >= numberOfModules.outdoor))
                                && (
                                    <FormInput
                                        name={`${product.key}_product_line_id`}
                                        type='searchable-select'
                                        label={crud?.models?.product_line
                                            || 'Product Line'
                                        }
                                        disabled={isProductLineDisabled(index)}
                                        options={selects.product_lines?.[product.key] || []}
                                        value={product.product_line_id}
                                        onChange={(value) => {
                                            if (typeof value !== 'string') return;
                                            handleProductLineChange(product, value);
                                        }}
                                        errorMessages={errors?.[`products.${index}.product_line_id`]}
                                        clearable={false}
                                    />
                                )
                            }

                            <FormInput
                                name={`${product.key}_product_id`}
                                type='searchable-select'
                                label={fields?.model
                                    || 'Model'
                                }
                                disabled={isProductModelDisabled(product, index)}
                                options={selects.product_models?.[product.key] || []}
                                value={product.product_id}
                                onChange={(val) => {
                                    if (typeof val !== 'string') return;
                                    
                                    if (
                                        productChild === PRODUCT_CHILDREN.RAS_MULTI
                                        && product.key === firstProductKey
                                    ) {
                                        const selectedModel = selects.product_models[product.key].find(
                                            model => model.value === val
                                        );

                                        if (!selectedModel) return;

                                        const numberOfIndoorUnits = Number(selectedModel.label.charAt(0));
                                        const totalNumberOfUnitsRequired = numberOfIndoorUnits + 1;

                                        if (nonA2aProducts.length !== totalNumberOfUnitsRequired) {
                                            setNumberOfUnits(totalNumberOfUnitsRequired);
                                            setValues({
                                                products: nonA2aProducts.map(prdct =>
                                                    prdct.key === product.key
                                                        ? { ...prdct, product_id: val }
                                                        : prdct
                                                )
                                            });
                                            return;
                                        }
                                    }

                                    const lastProductIndex = nonA2aProducts.length - 1;
                                    const lastProductKey = nonA2aProducts[lastProductIndex].key

                                    const updatedProducts = nonA2aProducts?.map((prdct) => {
                                        const shouldUpdateAllIndoorProducts = (
                                            productChild === PRODUCT_CHILDREN.RAV_MULTI
                                            && product.key !== lastProductKey
                                            && prdct.key !== lastProductKey
                                        );

                                        if (
                                            shouldUpdateAllIndoorProducts
                                            || prdct.key === product.key
                                        ) {
                                            return { ...prdct, product_id: val };
                                        }

                                        return prdct;
                                    });

                                    setValues({ products: updatedProducts });
                                }}
                                errorMessages={errors?.[`products.${index}.product_id`]}
                                clearable={false}
                            />

                            <FormInput
                                name={`${product.key}_serial_number`}
                                type='text'
                                label={StringHelpers.title(
                                    fields?.serial_number
                                    || 'serial number'
                                )}
                                disabled={disabled || isLoading}
                                value={product.serial_number}
                                onChange={(value) => {
                                    if (typeof value !== 'string') return;

                                    setValues({
                                        products: nonA2aProducts?.map((prdct) => 
                                            prdct.key === product.key
                                            ? { ...prdct, serial_number: value }
                                            : prdct
                                        )
                                    });
                                }}
                                errorMessages={errors?.[`products.${index}.serial_number`]}
                            />

                            <FormInput
                                name={`${product.key}_installation_location`}
                                type='text'
                                label={
                                    `${crud?.models?.installation 
                                        || 'Installation'} ${
                                    fields?.location 
                                        || 'Location'
                                    }`
                                }
                                disabled={disabled || isLoading}
                                value={product.installation_location}
                                onChange={(value) => {
                                    if (typeof value !== 'string') return;

                                    setValues({
                                        products: nonA2aProducts?.map((prdct) => 
                                            prdct.key === product.key
                                            ? { ...prdct, installation_location: value }
                                            : prdct
                                        )
                                    });
                                }}
                                errorMessages={errors?.[`products.${index}.installation_location`]}
                                noMargin={domesticHotWaterFitted 
                                    ? index <= 1
                                    : true
                                }
                            />

                            {productChild === PRODUCT_CHILDREN.ESTIA 
                                && domesticHotWaterFitted 
                                && index === nonA2aProducts.length - 1
                                && (
                                    <FormInput
                                        name={`${product.key}_water_circuit_description`}
                                        type='textarea'
                                        label={fields?.water_circuit_description}
                                        value={product.water_circuit_description}
                                        onChange={(value) => {
                                            if (typeof value !== 'string') return;
                                            
                                            setValues({
                                                products: nonA2aProducts.map((prdct) =>
                                                    prdct.key === product.key
                                                    ? { ...prdct, water_circuit_description: value }
                                                    : prdct
                                                )
                                            });
                                        }}
                                        noMargin
                                        disabled={disabled || isLoading}
                                        errorMessages={errors?.[`products.${index}.water_circuit_description`]}
                                    />
                                )
                            }
                        </CardBody>
                    </Card>
                    {productChild === PRODUCT_CHILDREN.ESTIA 
                        && index === 1
                        && (
                            <FormInput
                                className='mb-4'
                                name='domestic_hot_water_fitted'
                                type='checkbox'
                                value={domesticHotWaterFitted}
                                label={fields?.domestic_hot_water
                                    || 'Domestic Hot Water Fitted?'
                                }
                                onChange={(value) => {
                                    if (typeof value !== 'boolean') return;

                                    setNumberOfUnits(prev => 
                                        value ? prev + 1 : prev - 1
                                    );
                                }}
                                disabled={disabled || isLoading}
                            />
                        )
                    }
                </div>
            )}
            <Card>
                <CardBody>
                    <SubTitle
                        title={StringHelpers.title(
                            fields?.other
                                || 'other'
                        )}
                    />
                    <FormInput
                        name='total_system_refrigerant'
                        type='text'
                        inputMode='decimal'
                        value={store?.total_system_refrigerant}
                        label={StringHelpers.title(
                            fields?.total_system_refrigerant
                                || 'total system refrigerant'
                        )}
                        onChange={(value) => {
                            if (typeof value !== 'string') return;
                            if (!/^\d*\.?\d*$/.test(value)) return;
                            
                            setValues({
                                total_system_refrigerant: value
                            });
                        }}
                        disabled={disabled || isLoading}
                        errorMessages={errors?.total_system_refrigerant}
                    />
                    {productChild !== PRODUCT_CHILDREN.A2A &&
                        <FormInput
                            name='add_a2a'
                            type='checkbox'
                            value={store?.add_a2a}
                            onChange={(value) => {
                                if (typeof value !== 'boolean') return;

                                setValues({
                                    add_a2a: value
                                });
                            }}
                            label={fields?.a2a_checkbox
                                || 'Ventilation unit installed?'
                            }
                            disabled={disabled || isLoading}
                        />
                    }
                </CardBody>
            </Card>
        </Stepper.Step>
    )
};

export default ToshibaStep5;